import { Box3, Vector3 } from 'three';

import { CopcHierarchyNode, CopcHierarchyPage, CopcHierarchySubtree, CopcMetadata } from './types';

export interface CopcOctreeNode {
  /** depth-x-y-z, as the COPC hierarchy names its nodes. */
  key: string;
  depth: number;
  x: number;
  y: number;
  z: number;
  /** Zero for a node without points of its own, which can still have children. */
  hierarchy: CopcHierarchyNode;
  /** The node's cube cut to the points' bounds, relative to the point cloud's origo. */
  box: Box3;
  center: Vector3;
  /** The center of the node's whole cube, where its children split, relative to the origo. */
  cubeCenter: Vector3;
  cubeHalfSize: number;
  /** The distance between the node's points. */
  spacing: number;
}

export const ROOT_KEY = '0-0-0-0';

/**
 * The nodes of a COPC's octree that are known so far. The hierarchy is split into pages, and the
 * pages below the root page are loaded once traversal reaches them.
 */
export class CopcOctree {
  private nodes = new Map<string, CopcOctreeNode>();
  /** Pages that aren't loaded, by the key of the node they start at. */
  private pages = new Map<string, CopcHierarchyPage>();
  private bounds: Box3;
  private cubeMin: Vector3;
  private cubeSize: number;

  constructor(
    readonly metadata: CopcMetadata,
    origo: Vector3,
  ) {
    const { cube, min, max } = metadata;
    this.cubeMin = new Vector3(cube[0], cube[1], cube[2]).sub(origo);
    this.cubeSize = cube[3] - cube[0];
    this.bounds = new Box3(new Vector3(...min).sub(origo), new Vector3(...max).sub(origo));
  }

  get root(): CopcOctreeNode | undefined {
    return this.nodes.get(ROOT_KEY);
  }

  get(key: string) {
    return this.nodes.get(key);
  }

  addSubtree({ nodes, pages }: CopcHierarchySubtree) {
    for (const [key, hierarchy] of Object.entries(nodes)) {
      if (hierarchy) {
        this.nodes.set(key, this.createNode(key, hierarchy));
        this.pages.delete(key);
      }
    }
    for (const [key, page] of Object.entries(pages)) {
      if (page && !this.nodes.has(key)) {
        this.pages.set(key, page);
      }
    }
  }

  /** The children that are loaded, and the keys of those whose hierarchy page isn't. */
  children(node: CopcOctreeNode): { nodes: CopcOctreeNode[]; unloadedPages: string[] } {
    const nodes: CopcOctreeNode[] = [];
    const unloadedPages: string[] = [];

    for (let i = 0; i < 8; i++) {
      const key = `${node.depth + 1}-${node.x * 2 + (i & 1)}-${node.y * 2 + ((i >> 1) & 1)}-${
        node.z * 2 + ((i >> 2) & 1)
      }`;
      const child = this.nodes.get(key);
      if (child) {
        nodes.push(child);
      } else if (this.pages.has(key)) {
        unloadedPages.push(key);
      }
    }

    return { nodes, unloadedPages };
  }

  getPage(key: string) {
    return this.pages.get(key);
  }

  private createNode(key: string, hierarchy: CopcHierarchyNode): CopcOctreeNode {
    const [depth, x, y, z] = key.split('-').map(Number);
    const size = this.cubeSize / 2 ** depth;
    const min = new Vector3(x, y, z).multiplyScalar(size).add(this.cubeMin);
    const cube = new Box3(min, min.clone().addScalar(size));
    const box = cube.clone().intersect(this.bounds);

    return {
      key,
      depth,
      x,
      y,
      z,
      hierarchy,
      box,
      center: box.getCenter(new Vector3()),
      cubeCenter: cube.getCenter(new Vector3()),
      cubeHalfSize: size / 2,
      spacing: this.metadata.spacing / 2 ** depth,
    };
  }
}
